import * as Tooltip from '@radix-ui/react-tooltip';
import { CircleMinus, Download, Edit3, ExternalLink, FolderInput, MessageCircle, MoreHorizontal, TextCursor, Trash2, Waypoints } from 'lucide-react';
import type React from 'react';
import { useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import type {AffectedBundle} from "@/components/bundle/bundleTypes";
import { useOpenInCasePartner } from '@/components/case-partner/hooks/useOpenInCasePartner';
import { useDeepAnalysisCostConfirmation } from '@/components/deep-analysis/DeepAnalysisCostConfirmation';
import { isImageFileName, isPowerPointFileName, isSpreadsheetFileName, isTextFileName } from '@/utils/file-utils';
import { useMatterJurisdiction } from "@/components/contexts/MatterJurisdictionContext";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { Document } from '@/hooks/useDocuments';
import {
  canToggleDeepAnalysis,
  DEEP_ANALYSIS_LABEL,
  getDeepAnalysisTransitionLabel,
  setFileAnalysisSetState,
} from '@/hooks/useAnalysisSet';
import { apiErrorMessage, backendClient } from '@/lib/api-client';
import { documentNewTabUrl } from '@/lib/documentRoutes';
import { getSourceDisplayName } from '@/lib/sourceUtils';
import { downloadFile } from '@/lib/utils';
import type { DocumentDeleteOptions } from '@/types/document';
import { MoveDocumentsDialog } from '../document-types/MoveDocumentsDialog';
import { EditCaseFileDialog } from './EditCaseFileDialog';
import { EditCorrespondenceDialog } from './EditCorrespondenceDialog';
import { RenameDocumentDialog } from './RenameDocumentDialog';

interface DocumentActionsDropdownProps {
  document: Document;
  slug: string;
  fromPath: string;
  onRefresh: () => void | Promise<void>;
  isCorrespondence?: boolean;
  isBundle?: boolean;
  handleDelete: (documentIds: string[], options?: DocumentDeleteOptions) => Promise<void>;
  deepAnalysisEnabled?: boolean;
}

export const DocumentActionsDropdown: React.FC<DocumentActionsDropdownProps> = ({
  document,
  slug,
  fromPath,
  onRefresh,
  isCorrespondence = false,
  isBundle = false,
  handleDelete,
  deepAnalysisEnabled = false,
}) => {
  const openInCasePartner = useOpenInCasePartner();
  const {
    confirmDeepAnalysisCost,
    deepAnalysisCostConfirmationDialog,
  } = useDeepAnalysisCostConfirmation();
  const { ui_labels } = useMatterJurisdiction().jurisdiction;
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteAttachments, setDeleteAttachments] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isUpdatingAnalysisSet, setIsUpdatingAnalysisSet] = useState(false);
  const [affectedBundles, setAffectedBundles] = useState<AffectedBundle[]>([]);
  const deleteInFlightRef = useRef(false);

  const handleOpenInCasePartner = () => {
    openInCasePartner(slug, document);
  };
  const viewUrl = documentNewTabUrl(slug, document);

  const hasAttachments = (document.attachments?.length ?? 0) > 0;

  const openDeleteDialog = () => {
    setDeleteAttachments(hasAttachments);
    setDeleteDialogOpen(true);
  };

  const onDelete = async (force = false) => {
    if (deleteInFlightRef.current) return;

    deleteInFlightRef.current = true;
    setIsDeleting(true);

    try {
      await handleDelete([document.id], {
        force,
        deleteAttachments: hasAttachments && deleteAttachments,
      });
      setAffectedBundles([]);
      setDeleteDialogOpen(false);
    } catch (error: any) {
      const bundleWarning = error?.response?.status === 409 &&
        error?.response?.data?.detail?.warnings?.find((w: any) => w.type === 'bundle_references');
      if (bundleWarning) {
        setAffectedBundles(bundleWarning.affected_bundles);
        return;
      }
      toast.error(apiErrorMessage(error) || 'Failed to delete file');
    } finally {
      deleteInFlightRef.current = false;
      setIsDeleting(false);
    }
  };

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      // Get the download URL from the backend using secure get-file endpoint
      // Note: slug contains the matter GUID, document.id is the FileReference GUID
      const response = await backendClient.get(
        `/correspondence/${slug}/get-file?file_reference_guid=${document.id}&download=true`
      );
      const downloadInfo = response.data;

      const downloadFilename = downloadInfo.fileName || downloadInfo.displayName || 'download';
      await downloadFile(downloadInfo.signedUrl, downloadFilename);

      toast.success('Download started');
    } catch (error: any) {
      console.error('Download error:', error);
      toast.error('Failed to download file');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleToggleAnalysisSet = async () => {
    if (!deepAnalysisEnabled || !canToggleDeepAnalysis(document.analysisSetStatus)) return;

    const nextState = document.analysisSetEffective ? 'excluded' : 'included';
    if (nextState === 'included') {
      const confirmed = await confirmDeepAnalysisCost({
        documentCount: 1,
        tokenCount: document.token_count,
      });
      if (!confirmed) return;
    }

    setIsUpdatingAnalysisSet(true);
    try {
      await setFileAnalysisSetState(slug, document.id, nextState);
      toast.success(
        document.analysisSetEffective
          ? `Removed from ${DEEP_ANALYSIS_LABEL}`
          : `Marked for ${DEEP_ANALYSIS_LABEL}`,
      );
      await onRefresh();
    } catch (error: any) {
      console.error('Analysis Set update error:', error);
      toast.error(apiErrorMessage(error) || `Failed to update ${DEEP_ANALYSIS_LABEL}`);
    } finally {
      setIsUpdatingAnalysisSet(false);
    }
  };

  // Check if document can be moved/deleted based on source
  const canModifyDocument = document.source === 'crimson' || document.source === 'outlook';
  const isExternalSource = document.source && document.source !== 'crimson' && document.source !== 'outlook';
  // Check if the file is an Outlook-synced email (cannot be renamed since display name is derived from metadata)
  const isOutlookEmail = /\.(msg|eml)$/i.test(document.fileName || '') && document.source === 'outlook' && !document.isAttachment;
  const deepAnalysisTransitionLabel = getDeepAnalysisTransitionLabel(document.analysisSetStatus);
  const deepAnalysisToggleDisabled = isUpdatingAnalysisSet || Boolean(deepAnalysisTransitionLabel);

  return (
    <>
      {deepAnalysisCostConfirmationDialog}
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-8 w-8 p-0">
            <span className="sr-only">Open menu</span>
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="z-10000">
          <DropdownMenuItem asChild>
            <a
              href={document.is_drafting_managed && document.draft_document_guid
                ? viewUrl
                : `${viewUrl}?from=${encodeURIComponent(fromPath)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2"
            >
              <ExternalLink className="h-4 w-4" />
              View
            </a>
          </DropdownMenuItem>

          <DropdownMenuItem
            onClick={handleOpenInCasePartner}
            className="flex items-center gap-2"
          >
            <MessageCircle className="h-4 w-4" />
            Open in Case Partner
          </DropdownMenuItem>

          {deepAnalysisEnabled && (
            <DropdownMenuItem
              onClick={handleToggleAnalysisSet}
              disabled={deepAnalysisToggleDisabled}
              className="flex items-center gap-2"
            >
              {document.analysisSetEffective ? (
                <CircleMinus className="h-4 w-4" />
              ) : (
                <Waypoints className="h-4 w-4" />
              )}
              {isUpdatingAnalysisSet
                ? 'Updating...'
                : deepAnalysisTransitionLabel
                  ? deepAnalysisTransitionLabel
                  : document.analysisSetEffective
                    ? 'Remove from Deep Analysis'
                    : 'Mark for Deep Analysis'}
            </DropdownMenuItem>
          )}

          {/* Download action for processable documents */}
          {(document.fileName?.toLowerCase().endsWith('.pdf') ||
            document.fileName?.toLowerCase().endsWith('.docx') ||
            isPowerPointFileName(document.fileName) ||
            isSpreadsheetFileName(document.fileName) ||
            isImageFileName(document.fileName) ||
            isTextFileName(document.fileName) ||
            document.fileName?.toLowerCase().endsWith('.msg') ||
            document.fileName?.toLowerCase().endsWith('.eml')) && (
            <DropdownMenuItem 
              onClick={handleDownload}
              disabled={isDownloading}
              className="flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              {isDownloading ? 'Downloading...' : 'Download'}
            </DropdownMenuItem>
          )}
          
          <DropdownMenuItem
            onClick={() => setEditDialogOpen(true)}
            className="flex items-center gap-2"
          >
            <Edit3 className="h-4 w-4" />
            Edit
          </DropdownMenuItem>

          {canModifyDocument ? (
            <>
              {!isOutlookEmail && (
                <DropdownMenuItem
                  onClick={() => setRenameDialogOpen(true)}
                  className="flex items-center gap-2"
                >
                  <TextCursor className="h-4 w-4" />
                  Rename
                </DropdownMenuItem>
              )}

              <DropdownMenuItem
                onClick={() => setMoveDialogOpen(true)}
                className="flex items-center gap-2"
              >
                <FolderInput className="h-4 w-4" />
                Move
              </DropdownMenuItem>

              <DropdownMenuItem
                onClick={openDeleteDialog}
                className="flex items-center gap-2 text-red-600"
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </>
          ) : isExternalSource ? (
            <>
              <Tooltip.Provider>
                <Tooltip.Root delayDuration={300}>
                  <Tooltip.Trigger asChild>
                    <div>
                      <DropdownMenuItem disabled className="cursor-not-allowed">
                        <TextCursor className="mr-2 h-4 w-4" />
                        Rename
                      </DropdownMenuItem>
                    </div>
                  </Tooltip.Trigger>
                  <Tooltip.Portal>
                    <Tooltip.Content
                      className="z-10001 px-2 py-1 text-xs bg-gray-900 text-white rounded-sm shadow-lg max-w-xs"
                      sideOffset={5}
                    >
                      This document is synced from {getSourceDisplayName(document.source)} and cannot be renamed
                      <Tooltip.Arrow className="fill-gray-900" />
                    </Tooltip.Content>
                  </Tooltip.Portal>
                </Tooltip.Root>
              </Tooltip.Provider>
              <Tooltip.Provider>
                <Tooltip.Root delayDuration={300}>
                  <Tooltip.Trigger asChild>
                    <div>
                      <DropdownMenuItem disabled className="cursor-not-allowed">
                        <FolderInput className="mr-2 h-4 w-4" />
                        Move
                      </DropdownMenuItem>
                    </div>
                  </Tooltip.Trigger>
                  <Tooltip.Portal>
                    <Tooltip.Content
                      className="z-10001 px-2 py-1 text-xs bg-gray-900 text-white rounded-sm shadow-lg max-w-xs"
                      sideOffset={5}
                    >
                      This document is synced from {getSourceDisplayName(document.source)} and cannot be moved
                      <Tooltip.Arrow className="fill-gray-900" />
                    </Tooltip.Content>
                  </Tooltip.Portal>
                </Tooltip.Root>
              </Tooltip.Provider>
              <Tooltip.Provider>
                <Tooltip.Root delayDuration={300}>
                  <Tooltip.Trigger asChild>
                    <div>
                      <DropdownMenuItem disabled className="cursor-not-allowed">
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete
                      </DropdownMenuItem>
                    </div>
                  </Tooltip.Trigger>
                  <Tooltip.Portal>
                    <Tooltip.Content
                      className="z-10001 px-2 py-1 text-xs bg-gray-900 text-white rounded-sm shadow-lg max-w-xs"
                      sideOffset={5}
                    >
                      This document is synced from {getSourceDisplayName(document.source)} and cannot be deleted
                      <Tooltip.Arrow className="fill-gray-900" />
                    </Tooltip.Content>
                  </Tooltip.Portal>
                </Tooltip.Root>
              </Tooltip.Provider>
            </>
          ) : (
            <>
              {!isOutlookEmail && (
                <DropdownMenuItem
                  onClick={() => setRenameDialogOpen(true)}
                  className="flex items-center gap-2"
                >
                  <TextCursor className="h-4 w-4" />
                  Rename
                </DropdownMenuItem>
              )}

              <DropdownMenuItem
                onClick={() => setMoveDialogOpen(true)}
                className="flex items-center gap-2"
              >
                <FolderInput className="h-4 w-4" />
                Move
              </DropdownMenuItem>

              <DropdownMenuItem
                onClick={openDeleteDialog}
                className="flex items-center gap-2 text-red-600"
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {isCorrespondence ? (
        <EditCorrespondenceDialog
          row={document}
          onSuccess={onRefresh}
          isOpen={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
        />
      ) : (
        <EditCaseFileDialog
          row={document}
          onSuccess={onRefresh}
          isOpen={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
        />
      )}

      <RenameDocumentDialog
        row={document}
        onSuccess={onRefresh}
        isOpen={renameDialogOpen}
        onClose={() => setRenameDialogOpen(false)}
      />

      <MoveDocumentsDialog
        isOpen={moveDialogOpen}
        onClose={() => setMoveDialogOpen(false)}
        documentIds={[document.id]}
        onSuccess={onRefresh}
        currentFolderIds={document.document_type?.id ? [parseInt(document.document_type.id)] : []}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={(open) => { if (!open) { setDeleteDialogOpen(false); setAffectedBundles([]); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete &quot;{document.displayName || document.fileName || 'this file'}&quot; from Crimson.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {hasAttachments && (
            <div className="flex items-start gap-2 rounded-md border border-stone-200 bg-stone-50 p-3 text-sm">
              <Checkbox
                id={`delete-attachments-${document.id}`}
                checked={deleteAttachments}
                onCheckedChange={(checked) => setDeleteAttachments(checked === true)}
                disabled={isDeleting}
              />
              <label htmlFor={`delete-attachments-${document.id}`} className="leading-5 cursor-pointer">
                <span className="font-medium text-stone-900">Also delete attachments from this email</span>
                <span className="block text-stone-600">
                  Uncheck to delete only the email and keep its attachments.
                </span>
              </label>
            </div>
          )}
          {affectedBundles.length > 0 && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              <p className="font-medium mb-1">This file is used in the following {affectedBundles.length > 1 ? ui_labels.bundle_plural.toLowerCase() : ui_labels.bundle_singular.toLowerCase()}:</p>
              <ul className="list-disc pl-4 space-y-0.5">
                {affectedBundles.map(b => (
                  <li key={b.bundle_id}>{b.bundle_title}</li>
                ))}
              </ul>
              <p className="mt-1">Deleting it will remove it from {affectedBundles.length === 1 ? `this ${ui_labels.bundle_singular.toLowerCase()}` : `these ${ui_labels.bundle_plural.toLowerCase()}`}. The {affectedBundles.length > 1 ? ui_labels.bundle_plural.toLowerCase() : ui_labels.bundle_singular.toLowerCase()} may need to be regenerated.</p>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); onDelete(affectedBundles.length > 0); }}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600 text-white"
            >
              {isDeleting ? 'Deleting...' : affectedBundles.length > 0 ? 'Delete Anyway' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
